<template>
  <div
    ref="toolboxRef"
    v-outside-click="{ handler: hideToolbox, include }"
    class="kk-designer__toolbox"
    :class="{ 'kk-designer__toolbox-disabled': disabled }"
  >
    <div class="wrapper">
      <tooltip v-if="showTooltip" :text="'01'" />
      <button data-test-id="font-select-button" text dense class="select_font" @click="showContextMenu(1)">
        <span class="kk-designer__truncate" :style="{ fontFamily: selectedFontName }">
          {{ truncatedText }}
        </span>
      </button>
      <select-font v-if="activeButton === 1" @select-font="setFont" />
    </div>
    <div class="kk-designer__toolbox-fontsize">
      <tooltip v-if="showTooltip" :text="'02,03'" :style="{ width: '2.7rem' }" />
      <div>
        {{ fontSize }}
      </div>
      <div>
        <i data-test-id="increase-font-size" class="icon-menu-up" @click="increase"></i>
        <i data-test-id="decrease-font-size" class="icon-menu-down" @click="decrease"></i>
      </div>
    </div>
    <div class="wrapper">
      <tooltip v-if="showTooltip" :text="'04'" />
      <button data-test-id="bold-button" text dense @click="boldText">
        <i v-if="fontWeight === 'bold'" class="icon-format-bold"></i>
        <i v-else>B</i>
      </button>
    </div>
    <div class="wrapper">
      <tooltip v-if="showTooltip" :text="'05'" />
      <button data-test-id="alignment-button" text dense @click="showContextMenu(2)">
        <i :class="alignIcon"></i>
      </button>
      <alignments
        v-if="activeButton === 2"
        @hide="hideBox"
        @set-text-align="setTextAlign"
        @set-vertical-align="setVerticalAlign"
      />
    </div>
    <div class="wrapper">
      <tooltip v-if="showTooltip" :text="'06'" />
      <button data-test-id="color-button" text dense @click="showContextMenu(3, true)">
        <i class="icon-eyedropper-variant"></i>
      </button>
      <colors v-if="activeButton === 3" :colors="selectedColors" @select-color="setColor" @hide="hideBox" />
    </div>
    <div class="wrapper">
      <tooltip v-if="showTooltip" :text="'07'" />
      <button data-test-id="bg-color-button" text dense @click="showContextMenu(4, true)">
        <i class="icon-format-color-highlight"></i>
      </button>
      <colors v-if="activeButton === 4" :colors="selectedColors" @select-color="setColor" @hide="hideBox" />
    </div>
    <div class="wrapper">
      <tooltip v-if="showTooltip" :text="'08'" />
      <button data-test-id="clear-format-button" text dense @click="clearFormat">
        <i class="icon-format-color-marker-cancel"></i>
      </button>
    </div>
    |
    <div class="wrapper">
      <tooltip v-if="showTooltip" :text="'09'" />
      <button data-test-id="cellIcon-button" text dense @click="showContextMenu(5, true)">
        <i class="icon-simple-icons"></i>
      </button>
      <cell-icons v-if="activeButton === 5" @select-icon="setIcon" @hide="hideBox" />
    </div>
  </div>
</template>

<script>
import DesignerMixin from "../../DesignerMixin";
import SelectFont from "./SelectFont.vue";
import Colors from "./Colors.vue";
import hotkeys from "hotkeys-js";
import Tooltip from "../../../commons/Tooltip.vue";
import Alignments from "./Alignments.vue";
import CellIcons from "./CellIcons.vue";
import { HotkeyScopes, TextColors, BgColors } from "../../../../utils/constants";
import { icons } from "../../../../utils/icons";
export default {
  components: {
    SelectFont,
    Colors,
    Tooltip,
    Alignments,
    CellIcons,
  },
  mixins: [DesignerMixin],
  props: ["styleIndex"],
  emits: ["select-font", "select-icon", "select-color", "set-text-align", "set-vertical-align", "hide"],
  data() {
    return {
      fontSize: 12,
      fontWeight: "",
      activeButton: null,
      highlighted: false,
      selectedFontName: "",
      maxLength: 15,
      selectedColors: "",
      showTooltip: false,
      keyPressed: null,
    };
  },
  computed: {
    truncatedText() {
      if (this.selectedFontName.length > this.maxLength) {
        return this.selectedFontName.substring(0, this.maxLength) + "...";
      } else {
        return this.selectedFontName;
      }
    },
    alignIcon() {
      const firstCellStyle =
        this.$store.getters["rangeStyle/firstCellStyle"](this.mainRange?.range[0], this.mainRange?.range[1]) || {};
      return firstCellStyle?.textAlign === "center"
        ? "icon-format-align-center"
        : firstCellStyle?.textAlign === "right"
        ? "icon-format-align-right"
        : "icon-format-align-left";
    },
    disabled() {
      return !(this.mainRange?.range || this.$store.state.preferences.editableStyleId);
    },
  },
  mounted() {
    this.getData(this.design.cells);
    if (hotkeys.getScope() === HotkeyScopes.QuickAccessToolbar) this.showTooltip = true; // show tooltip just in case of keyboard.

    this.$store.dispatch("setScope", HotkeyScopes.EditToolBox);
    hotkeys("*", HotkeyScopes.EditToolBox, (event, handler) => {
      if (event.key === "Escape") {
        this.hideToolbox();
      }
      event.preventDefault();
      const isNumKey = event.code.startsWith("Numpad") || event.code.startsWith("Digit");
      if (!isNumKey) return;

      if (this.keyPressed === null && event.key === "0") return (this.keyPressed = event.key);
      const shortCut = (this.keyPressed ?? "") + event.key;

      switch (this.unformatNumber(+shortCut) + "") {
        case "1":
          this.showContextMenu(1);
          this.showTooltip = false;
          break;
        case "2":
          this.increase();
          break;
        case "3":
          this.decrease();
          break;
        case "4":
          this.boldText();
          break;
        case "5":
          this.showContextMenu(2);
          this.showTooltip = false;
          break;
        case "6":
          this.showContextMenu(3, false);
          this.showTooltip = false;
          break;
        case "7":
          this.showContextMenu(4, true);
          this.showTooltip = false;
          break;
        case "8":
          this.clearFormat();
          break;
        case "9":
          this.showContextMenu(5, true);
          this.showTooltip = false;
          break;
      }
      // reset values.
      this.keyPressed = null;
    });
  },
  beforeUnmount() {
    hotkeys.unbind("*", HotkeyScopes.EditToolBox);
    this.$store.dispatch("setScope", HotkeyScopes.ProcessDesigner);
  },
  methods: {
    increase() {
      if (this.fontSize < 20) {
        this.fontSize++;
        this.setFontSize();
      }
    },
    decrease() {
      if (this.fontSize > 10) {
        this.fontSize--;
        this.setFontSize();
      }
    },
    setFont(fontFace, showTooltip) {
      this.selectedFontName = fontFace;
      this.activeButton = null;
      this.showTooltip = showTooltip;
      this.setData({ fontFamily: fontFace });
    },
    setFontSize() {
      this.setData({ fontSize: this.fontSize });
    },
    setColor(color, showTooltip) {
      if (this.highlighted && this.activeButton === 4) this.setData({ bgColor: color });
      else if (this.highlighted && this.activeButton === 3) {
        this.setData({ color: color });
      }
      this.showTooltip = showTooltip;
      this.activeButton = null;
    },
    setIcon(icon, showTooltip) {
      this.activeButton = null;
      this.showTooltip = showTooltip;
      const iconName = Object.keys(icons).find((key) => icons[key] === icon);
      this.$store.dispatch("cells/updateIcon", iconName);
    },
    boldText() {
      this.fontWeight = this.fontWeight === "bold" ? "normal" : "bold";
      this.setData({ fontWeight: this.fontWeight });
    },
    setTextAlign(align, showTooltip) {
      this.activeButton = null;
      this.showTooltip = showTooltip;
      this.setData({ textAlign: align });
    },
    setVerticalAlign(align, showTooltip) {
      this.activeButton = null;
      this.showTooltip = showTooltip;
      this.setData({ verticalAlign: align });
    },
    showContextMenu(buttonNumber, highlight) {
      if (this.activeButton === buttonNumber) {
        this.activeButton = null;
        return;
      }
      this.activeButton = buttonNumber;
      this.highlighted = false;
      this.selectedColors = TextColors;
      if (highlight) {
        this.highlighted = true;
        this.selectedColors = BgColors.map((item) => item.code);
      }
    },
    clearFormat() {
      this.$store.dispatch("rangeStyle/clearStyleFormats");
    },
    setData(data) {
      const styleItemId = this.preferences.editableStyleId;
      if (styleItemId) {
        const index = this.preferences.allPreferences.styles.findIndex((style) => style.id == styleItemId);
        return this.$store.dispatch("preferences/updateStyle", { index, data });
      }
      this.$store.dispatch("rangeStyle/updateRangeStyle", { data });
    },
    getData() {
      const firstCellStyle =
        this.$store.getters["rangeStyle/firstCellStyle"](this.mainRange?.range[0], this.mainRange?.range[1]) || {};

      this.selectedFontName = firstCellStyle.fontFamily || "Roboto";
      this.fontSize = firstCellStyle.fontSize || 14;
      this.fontWeight = firstCellStyle.fontWeight || "normal";
    },
    include() {
      return [...document.querySelectorAll(".style-item-input")];
    },
    hideToolbox() {
      this.$store.dispatch("preferences/setEditableStyleId", null);
      this.$store.dispatch("preferences/toggleShowEditToolbox", false);
    },
    unformatNumber(number) {
      return number[0] === 0 ? number[1] : number;
    },
    hideBox() {
      this.activeButton = null;
      this.showTooltip = true;
    },
  },
};
</script>
<style lang="less" scoped>
@import "../../../../assets/styles/variables.less";
.kk-designer__toolbox {
  display: flex;
  align-items: center;
  position: absolute;
  transform: translate(0, 110%);
  z-index: 42;
  border-radius: 8px;
  box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
  border: 0.5px solid lightgray;
  background-color: @kk-color-background;
  height: 3rem;
  width: fit-content;
  :deep(i) {
    font-size: 20px;
  }
  &-fontsize {
    display: flex;
    gap: 20px;
    align-items: center;
    :last-child {
      display: flex;
      flex-direction: column;
      i {
        font-size: 15px;
      }
    }
  }
  &__truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &-disabled {
    background-color: @kk-color-lighter-gray;
    color: @kk-color-gray-1;
    button {
      color: @kk-color-gray-1;
      text-transform: none;
    }
    cursor: auto;
    pointer-events: none;
  }
}
:deep(button) {
  color: @kk-color-primary-blue;
  text-transform: none;
  :hover {
    color: @kk-color-gold;
  }
}

:deep(.wrapper) {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  min-width: 3rem;
  .select_font {
    width: 10rem;
  }
}
</style>
